import request from './request'
import { isCouponApiSuccess } from './couponApi'
import { getMockCouponUpdateVisibilityResponse, shouldUseCouponMock } from './couponCenterMock'
import {
  formatSpecifiedUsersFilePathForApi,
  uploadCouponSpecifiedUsersTable,
} from './CouponTableUpload'
import type { CouponDetailData } from './CouponDetai'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export type CouponVisibilityValue = 'visible' | 'hidden'
export type CouponVisibleToValue = 'all_users' | 'specified_users'

export interface CouponSetVisibilityParams {
  id: number
  coupon_id: string
  visibility: CouponVisibilityValue
  visible_to?: CouponVisibleToValue
  file?: File | null
}

export type CouponSetVisibilityData = CouponDetailData

export function mapVisibleToForApi(value?: string): CouponVisibleToValue {
  const normalized = (value ?? '').trim().toLowerCase()
  if (
    normalized === 'specified' ||
    normalized === 'specified_user' ||
    normalized === 'specified_users' ||
    normalized === 'specified users'
  ) {
    return 'specified_users'
  }
  if (normalized === 'all_users' || normalized === 'all users') {
    return 'all_users'
  }
  return 'all_users'
}

export function mapVisibleToFromApi(value?: string): CouponVisibleToValue {
  return mapVisibleToForApi(value)
}

/** 请求体：all_users | specified_users */
function mapVisibleToBodyValue(value?: string): string {
  return mapVisibleToForApi(value) === 'specified_users' ? 'specified_users' : 'all_users'
}

function buildJsonBody(
  params: CouponSetVisibilityParams,
  specifiedUsersFilePath?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    id: params.id,
    visibility: params.visibility,
  }

  const couponId = params.coupon_id.trim()
  if (couponId) {
    body.coupon_id = couponId
  }

  if (params.visibility === 'visible') {
    body.visible_to = mapVisibleToBodyValue(params.visible_to)
  }

  if (specifiedUsersFilePath?.trim()) {
    body.specified_users_file = formatSpecifiedUsersFilePathForApi(specifiedUsersFilePath)
  }

  return body
}

async function postVisibilityJsonOnce(
  body: Record<string, unknown>,
): Promise<ApiResponse<CouponSetVisibilityData>> {
  return request.post<ApiResponse<CouponSetVisibilityData>>(
    '/api/admin/coupon/update-visibility',
    body,
  )
}

/** Apifox：① upload 拿 path  ② update-visibility JSON */
async function postVisibilityWithTableFile(
  params: CouponSetVisibilityParams,
  file: File,
): Promise<ApiResponse<CouponSetVisibilityData>> {
  const uploadedPath = await uploadCouponSpecifiedUsersTable(file)
  return postVisibilityJsonOnce(buildJsonBody(params, uploadedPath))
}

/** POST `/api/admin/coupon/update-visibility` */
export async function postCouponSetVisibility(
  params: CouponSetVisibilityParams,
): Promise<ApiResponse<CouponSetVisibilityData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponUpdateVisibilityResponse(params) as ApiResponse<CouponSetVisibilityData>
  }

  const needsTableFile =
    params.visibility === 'visible' &&
    mapVisibleToForApi(params.visible_to) === 'specified_users' &&
    !!params.file

  if (needsTableFile && params.file) {
    return postVisibilityWithTableFile(params, params.file)
  }

  return postVisibilityJsonOnce(buildJsonBody(params, ''))
}

export function readCouponSetVisibilityError(error: unknown, fallback = ''): string {
  if (error && typeof error === 'object') {
    const record = error as { msg?: unknown; message?: unknown }
    if (typeof record.msg === 'string' && record.msg.trim()) return record.msg.trim()
    if (typeof record.message === 'string' && record.message.trim()) return record.message.trim()
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim()
  return fallback
}
